/**
 * @file Weapon servicing: equipment, aim resolution and shot execution.
 *
 * The system is the weapon's hands, not its brain. Every frame it:
 *  1. equips any weapon that has just been picked up or spawned;
 *  2. resolves where its owner is aiming and where the muzzle sits;
 *  3. ticks the weapon so cadence and reload advance in real time;
 *  4. forwards one fire request when the trigger asks for it.
 *
 * Whether that request becomes a shot is entirely the weapon's decision (see
 * {@link ../components/Weapon.js Weapon#tryFire}). When it does, the weapon
 * calls back into the executor below, which is the only place that knows how to
 * reach the world: spawning a projectile, or tracing a hitscan ray against the
 * same layer masks the collision system uses.
 *
 * Buffs are applied at the point of use rather than written into the weapon, so
 * an expiring buff can never leave a stat permanently altered.
 */

import * as THREE from 'three';
import { System } from '../ecs/System.js';
import {
  Transform,
  Weapon,
  Collider,
  Health,
  Projectile,
  PlayerControlled,
  AIBrain,
  RangedAI,
  FireResult,
  FireMode,
  ShotType,
  statMultiplier,
  TimedStatus,
  timedStatusRemaining
} from '../components/index.js';
import { Stat } from '../config/Powerups.js';
import { createBullet } from '../entities/Bullet.js';
import { createRocket } from '../entities/Rocket.js';
import { createShot } from '../combat/Shot.js';
import { Events } from '../core/EventBus.js';
import { PALETTE } from '../config/Palette.js';
import { raycastAABB, faceNormal } from '../physics/AABB.js';

export class WeaponSystem extends System {
  // Runs after the controllers have set aim and trigger (10, 20) and before
  // movement (40) integrates the projectiles it spawns.
  static priority = 25;

  constructor() {
    super();
    /** @type {THREE.Vector3} Owner aim direction, reused. @private */
    this._aim = new THREE.Vector3();
    /** @type {THREE.Vector3} Per-pellet direction after spread. @private */
    this._pellet = new THREE.Vector3();
    /** @type {THREE.Vector3} @private */
    this._hitPoint = new THREE.Vector3();
    /** @type {THREE.Vector3} @private */
    this._hitNormal = new THREE.Vector3();
    /** @type {{ x: number, z: number }} @private */
    this._normal = { x: 0, z: 0 };
    /**
     * Traced hits for the shot being resolved, sorted near-to-far.
     * @type {{ t: number, entity: import('../ecs/Entity.js').Entity }[]}
     * @private
     */
    this._hits = [];
    /**
     * Backing storage for `_hits`. Both arrays only ever grow, so a hitscan
     * shot allocates nothing after the first few.
     * @type {{ t: number, entity: import('../ecs/Entity.js').Entity }[]}
     * @private
     */
    this._hitPool = [];

    /** @type {object} @private */
    this._flashEvent = {
      position: this._hitPoint,
      color: PALETTE.muzzle,
      intensity: 9,
      life: 0.06
    };
    /** @type {object} @private */
    this._shakeEvent = { amount: 0 };
    /** @type {object} @private */
    this._impactEvent = { position: this._hitPoint, normal: this._hitNormal, target: null };
    /** @type {{ x: number, z: number }} Reused hitscan trace direction. @private */
    this._damageDirection = { x: 0, z: 0 };
    /**
     * A hitscan hit is gunfire like any other: it carries the `'bullet'` type so
     * destructibles judge it by their `destroyedBy` list, and its trace
     * direction so the impact can shove what it hit (issue #75).
     * @type {object} @private
     */
    this._damageEvent = {
      target: null,
      amount: 0,
      source: null,
      point: this._hitPoint,
      type: 'bullet',
      direction: this._damageDirection,
      impulse: 0
    };
    /** @type {object} @private */
    this._weaponHitEvent = { weapon: null, target: null, position: this._hitPoint, damage: 0 };
    /** @type {object} @private */
    this._missEvent = { weapon: null, owner: null };

    /**
     * Bound once and handed to every weapon at equip time.
     * @type {import('../components/Weapon.js').ShotExecutor}
     * @private
     */
    this._executor = (weapon, direction, context) => this._executeShot(weapon, direction, context);
  }

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    for (const entity of this.world.query(Weapon, Transform)) {
      const weapon = entity.get(Weapon);

      // A weapon that just spawned — or that a pickup swapped in — belongs to
      // whoever carries it now. Factories equip with owner only, so a weapon
      // without services also passes through here to receive its executor.
      if (weapon.owner !== entity || !weapon.hasExecutor) {
        weapon.equip(entity, { events: this.ctx.events, executor: this._executor });
      }

      this._resolveAim(entity, weapon);
      weapon.update(dt);
      this._serviceTrigger(entity, weapon);
    }
  }

  /**
   * Points the weapon where its owner is looking and parks the muzzle there.
   *
   * The aim source belongs to the owner, never to the weapon: the player aims
   * at the cursor (or the right stick, already projected into `aimPoint`), an
   * agent aims at whatever its brain picked, and anything else simply fires
   * where its body faces.
   *
   * @param {import('../ecs/Entity.js').Entity} entity Weapon carrier.
   * @param {import('../components/Weapon.js').Weapon} weapon Its weapon.
   * @returns {void}
   * @private
   */
  _resolveAim(entity, weapon) {
    const transform = entity.get(Transform);
    const aimPoint = entity.get(PlayerControlled)?.aimPoint ?? this._brainTarget(entity);

    if (aimPoint) {
      const dx = aimPoint.x - transform.position.x;
      const dz = aimPoint.z - transform.position.z;
      if (dx * dx + dz * dz > 1e-6) this._aim.set(dx, 0, dz).normalize();
      else this._facing(transform, this._aim);
    } else {
      this._facing(transform, this._aim);
    }

    weapon.aimDirection.copy(this._aim);
    weapon.muzzle
      .copy(transform.position)
      .addScaledVector(this._aim, weapon.muzzleForward);
    weapon.muzzle.y = weapon.muzzleHeight;
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity Weapon carrier.
   * @returns {THREE.Vector3 | null} Where the agent's brain wants to shoot.
   * @private
   */
  _brainTarget(entity) {
    const target = entity.get(AIBrain)?.target;
    if (!target || !target.alive) return null;
    return target.get(Transform)?.position ?? null;
  }

  /**
   * @param {import('../components/Transform.js').Transform} transform Owner transform.
   * @param {THREE.Vector3} out Result, mutated in place.
   * @returns {THREE.Vector3} The same `out` vector.
   * @private
   */
  _facing(transform, out) {
    return out.set(Math.sin(transform.rotationY), 0, Math.cos(transform.rotationY));
  }

  /**
   * Turns held-trigger state into at most one request per frame, then lets the
   * owner react to the answer.
   *
   * @param {import('../ecs/Entity.js').Entity} entity Weapon carrier.
   * @param {import('../components/Weapon.js').Weapon} weapon Its weapon.
   * @returns {void}
   * @private
   */
  _serviceTrigger(entity, weapon) {
    // Streaming weapons are the stream system's trigger: it ticks tryFire at
    // its own cadence and sweeps the cone itself. A melee weapon is the same
    // arrangement with `MeleeWeaponSystem`, which swings the blade and never
    // wants the muzzle flash and rounds a shot would bring with it (issue #122).
    if (weapon.streaming || weapon.melee) return;

    const held = weapon.triggerHeld;
    // Semi-automatic asks once per press; automatic asks while held. Neither
    // can outrun the cadence, which the weapon enforces on its own.
    const wantsToFire = weapon.fireMode === FireMode.AUTO ? held : held && !weapon.triggerWasHeld;
    weapon.triggerWasHeld = held;
    if (!wantsToFire) return;

    const result = weapon.tryFire(this._aim, {
      rateMultiplier: statMultiplier(entity, Stat.FIRE_RATE),
      damageMultiplier: statMultiplier(entity, Stat.DAMAGE),
      unlimitedAmmo: timedStatusRemaining(entity, TimedStatus.UNLIMITED_AMMO) > 0
    });

    // An agent has nobody to press its reload key: an empty magazine is the
    // signal to reload, which is exactly the reaction the result enables.
    if (result === FireResult.NO_AMMO && (entity.has(AIBrain) || entity.has(RangedAI))) {
      weapon.reload();
    }
  }

  // ------------------------------------------------------------- shot execution

  /**
   * Resolves one valid shot. Called by the weapon, never directly: by the time
   * it runs, ammo has been spent and the cadence stamped.
   *
   * @param {import('../components/Weapon.js').Weapon} weapon Firing weapon.
   * @param {THREE.Vector3} direction Normalised aim direction, before spread.
   * @param {{ damage: number, accuracyMultiplier: number }} context Shot context.
   * @returns {void}
   * @private
   */
  _executeShot(weapon, direction, { damage, accuracyMultiplier }) {
    // A stream spawns nothing and paints its own FX; the sweep that follows
    // the successful tryFire is the shot.
    if (weapon.streaming) return;

    const owner = weapon.owner;

    // Every pellet of this trigger pull shares one damage ledger, so a
    // multi-pellet blast never exceeds the weapon's per-target ceiling.
    const shot = createShot(weapon.maxDamagePerTarget);

    for (let pellet = 0; pellet < weapon.pellets; pellet++) {
      weapon.applySpread(direction, this._pellet, accuracyMultiplier);

      if (weapon.shotType === ShotType.HITSCAN) {
        this._traceHitscan(weapon, owner, this._pellet, damage);
      } else if (weapon.explosive) {
        // Explosive rounds are rockets: the blast block rides the entity and
        // the collision system detonates it on impact.
        createRocket(this.ctx, {
          position: weapon.muzzle,
          direction: this._pellet,
          weapon,
          owner,
          damage,
          explosionDamage: weapon.explosive.damage * statMultiplier(owner, Stat.DAMAGE)
        });
      } else {
        createBullet(this.ctx, {
          position: weapon.muzzle,
          direction: this._pellet,
          weapon,
          owner,
          damage,
          shot
        });
      }
    }

    // Payload vectors are reused: listeners must copy what they keep.
    this._flashEvent.position = weapon.muzzle;
    this.ctx.events.emit(Events.FLASH, this._flashEvent);

    if (weapon.shake > 0) {
      this._shakeEvent.amount = weapon.shake;
      this.ctx.events.emit(Events.SHAKE, this._shakeEvent);
    }
  }

  /**
   * Traces one instant shot and damages what it reaches. Obstacles stop it: the
   * first body without health — a wall, a crate — ends the trace, and a target
   * with health only lets it through while the weapon still has pierce left.
   *
   * @param {import('../components/Weapon.js').Weapon} weapon Firing weapon.
   * @param {import('../ecs/Entity.js').Entity} owner Shooter.
   * @param {THREE.Vector3} direction Normalised pellet direction.
   * @param {number} damage Damage after buffs.
   * @returns {void}
   * @private
   */
  _traceHitscan(weapon, owner, direction, damage) {
    const originX = weapon.muzzle.x;
    const originZ = weapon.muzzle.z;
    const dx = direction.x * weapon.range;
    const dz = direction.z * weapon.range;

    const hits = this._collectHits(weapon, owner, originX, originZ, dx, dz);
    if (hits.length === 0) {
      this._missEvent.weapon = weapon;
      this._missEvent.owner = owner;
      this.ctx.events.emit(Events.WEAPON_MISSED, this._missEvent);
      return;
    }

    let pierce = weapon.pierce;
    for (const hit of hits) {
      const target = hit.entity;
      const damageable = target.has(Health);

      this._hitPoint.set(originX + dx * hit.t, weapon.muzzle.y, originZ + dz * hit.t);
      this._faceNormalOf(target);

      if (damageable) {
        this._damageEvent.target = target;
        this._damageEvent.amount = damage;
        this._damageEvent.source = owner;
        this._damageDirection.x = direction.x;
        this._damageDirection.z = direction.z;
        this._damageEvent.impulse = this.ctx.config.knockback.bulletImpulse * weapon.knockback;
        this.ctx.events.emit(Events.DAMAGE, this._damageEvent);
      }

      weapon.lastHitPosition.copy(this._hitPoint);
      weapon.lastHitTarget = target;
      weapon.lastDamageDealt = damageable ? damage : 0;

      this._impactEvent.target = target;
      this.ctx.events.emit(Events.BULLET_HIT, this._impactEvent);

      this._weaponHitEvent.weapon = weapon;
      this._weaponHitEvent.target = target;
      this._weaponHitEvent.damage = damageable ? damage : 0;
      this.ctx.events.emit(Events.WEAPON_HIT, this._weaponHitEvent);

      if (!damageable || pierce <= 0) return;
      pierce--;
    }
  }

  /**
   * Gathers everything the trace crosses, near to far. Insertion-sorted as it
   * goes: a trace crosses a handful of bodies at most, and this keeps the
   * pooled entries in place instead of re-sorting fresh objects.
   *
   * @param {import('../components/Weapon.js').Weapon} weapon Firing weapon.
   * @param {import('../ecs/Entity.js').Entity} owner Shooter.
   * @param {number} originX Muzzle X.
   * @param {number} originZ Muzzle Z.
   * @param {number} dx Trace vector X, already scaled to the weapon range.
   * @param {number} dz Trace vector Z, already scaled to the weapon range.
   * @returns {{ t: number, entity: import('../ecs/Entity.js').Entity }[]} Reused array.
   * @private
   */
  _collectHits(weapon, owner, originX, originZ, dx, dz) {
    const hits = this._hits;
    let count = 0;

    for (const other of this.world.query(Transform, Collider)) {
      // The shooter is never its own target, and rounds ignore each other.
      if (other === owner || !other.alive || other.has(Projectile)) continue;

      const collider = other.get(Collider);
      if (collider.isTrigger) continue;
      // Same mutual layer test the collision system applies to projectiles, so
      // hitscan and projectile weapons agree on what counts as a target.
      if ((weapon.projectileMask & collider.layer) === 0) continue;
      if ((collider.mask & weapon.projectileLayer) === 0) continue;
      if (!weapon.friendlyFireEnabled && this._isAlly(owner, other)) continue;

      const position = other.get(Transform).position;
      const t = raycastAABB(
        originX,
        originZ,
        dx,
        dz,
        position.x - collider.halfX,
        position.z - collider.halfZ,
        position.x + collider.halfX,
        position.z + collider.halfZ
      );
      if (t < 0 || t > 1) continue;

      const entry = this._hitPool[count] ?? (this._hitPool[count] = { t: 0, entity: null });
      entry.t = t;
      entry.entity = other;

      let index = count;
      while (index > 0 && hits[index - 1].t > t) {
        hits[index] = hits[index - 1];
        index--;
      }
      hits[index] = entry;
      count++;
    }

    hits.length = count;
    return hits;
  }

  /**
   * Faction check. Without a faction system, "ally" is simply "same kind of
   * combatant as the shooter", which is what friendly fire has to mean here.
   *
   * @param {import('../ecs/Entity.js').Entity | null} owner Shooter.
   * @param {import('../ecs/Entity.js').Entity} other Candidate target.
   * @returns {boolean}
   * @private
   */
  _isAlly(owner, other) {
    if (!owner) return false;
    if (owner === other) return true;
    if (owner.has(AIBrain) && other.has(AIBrain)) return true;
    return owner.has(PlayerControlled) && other.has(PlayerControlled);
  }

  /**
   * Orients the impact normal against the face the trace entered through.
   * @param {import('../ecs/Entity.js').Entity} target Entity that was hit.
   * @returns {void}
   * @private
   */
  _faceNormalOf(target) {
    const collider = target.get(Collider);
    const position = target.get(Transform).position;
    faceNormal(
      this._hitPoint.x,
      this._hitPoint.z,
      position.x,
      position.z,
      collider.halfX + 1e-4,
      collider.halfZ + 1e-4,
      this._normal
    );
    this._hitNormal.set(this._normal.x, 0, this._normal.z);
  }
}
